import { supabase } from '@/integrations/supabase/client';

export interface ChildConnectionCode {
  id: string;
  child_user_id: string;
  connection_code: string;
  is_active: boolean;
  expires_at: string;
  created_at: string;
}

export interface ConnectionResult {
  success: boolean;
  error?: string;
  child_username?: string;
  child_id?: string;
}

export const childConnectionService = {
  /**
   * Get connection code for a child user
   */
  async getChildConnectionCode(childUserId: string): Promise<ChildConnectionCode | null> {
    try {
      const { data, error } = await supabase
        .from('child_connection_codes')
        .select('*')
        .eq('child_user_id', childUserId)
        .eq('is_active', true)
        .gt('expires_at', new Date().toISOString())
        .maybeSingle();

      if (error) {
        console.error('Error fetching connection code:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getChildConnectionCode:', error);
      return null;
    }
  },

  /**
   * Generate a new connection code for a child (regenerate)
   */
  async generateNewConnectionCode(childUserId: string): Promise<string | null> {
    try {
      // Use the new database function that handles deduplication properly
      const { data, error } = await supabase.rpc('get_or_create_child_code', {
        p_child_user_id: childUserId
      });
      
      if (error) {
        console.error('Error generating/getting code:', error);
        return null;
      }

      return data as string;
    } catch (error) {
      console.error('Error in generateNewConnectionCode:', error);
      return null;
    }
  },

  /**
   * Get existing code or create new one (without unnecessary regeneration)
   */
  async getOrCreateChildCode(childUserId: string): Promise<string | null> {
    try {
      // Use the new database function that prevents unnecessary regeneration
      const { data, error } = await supabase.rpc('get_or_create_child_code', {
        p_child_user_id: childUserId
      });
      
      if (error) {
        console.error('Error getting/creating code:', error);
        return null;
      }

      return data as string;
    } catch (error) {
      console.error('Error in getOrCreateChildCode:', error);
      return null;
    }
  },

  /**
   * Connect parent to child using connection code
   */
  async connectParentToChild(parentId: string, connectionCode: string): Promise<ConnectionResult> {
    try {
      console.log('🔗 Attempting to connect parent:', parentId, 'with code:', connectionCode);
      
      // First check if this connection already exists
      const { data: existingConnection } = await supabase
        .from('parent_child_connections')
        .select('*')
        .eq('parent_id', parentId)
        .eq('connection_code', connectionCode.trim().toUpperCase())
        .maybeSingle();

      if (existingConnection) {
        console.log('⚠️ Connection already exists:', existingConnection);
        return {
          success: false,
          error: 'You are already connected to this child.'
        };
      }

      const { data, error } = await supabase.rpc('connect_parent_to_child', {
        p_parent_id: parentId,
        p_connection_code: connectionCode.trim().toUpperCase()
      });

      console.log('🔗 RPC response data:', data);
      console.log('❓ RPC response error:', error);

      if (error) {
        console.error('Error connecting parent to child:', error);
        
        // Provide more specific error messages
        if (error.message?.includes('expired')) {
          return {
            success: false,
            error: 'This connection code has expired. Please ask the child for a new code.'
          };
        } else if (error.message?.includes('not found') || error.message?.includes('invalid')) {
          return {
            success: false,
            error: 'Invalid connection code. Please check the code and try again.'
          };
        } else if (error.message?.includes('already connected')) {
          return {
            success: false,
            error: 'You are already connected to this child.'
          };
        }
        
        return {
          success: false,
          error: error.message || 'Failed to connect. Please try again.'
        };
      }

      return data as any as ConnectionResult;
    } catch (error) {
      console.error('Error in connectParentToChild:', error);
      return {
        success: false,
        error: 'Connection failed. Please check the code and try again.'
      };
    }
  },

  /**
   * Get connected children for a parent
   */
  async getConnectedChildren(parentId: string) {
    try {
      console.log('🔍 SERVICE - Fetching connections for parent:', parentId);
      console.log('🔍 SERVICE - Parent ID details:', {
        value: parentId,
        type: typeof parentId,
        length: parentId?.length,
        isEmpty: !parentId || parentId.trim() === ''
      });

      if (!parentId || parentId.trim() === '') {
        console.log('❌ SERVICE - Invalid parent ID provided');
        return [];
      }

      // Get connections
      const { data: connections, error: connectionsError } = await supabase
        .from('parent_child_connections')
        .select('*')
        .eq('parent_id', parentId)
        .eq('is_active', true);

      console.log('📊 Raw connection data:', connections);
      console.log('❓ Connection query error:', connectionsError);

      if (connectionsError) {
        console.error('❌ SERVICE - Database error:', connectionsError);
        throw connectionsError;
      }

      if (!connections || connections.length === 0) {
        console.log('📭 No connections found for parent:', parentId);
        return [];
      }

      // Get child details for each connection
      const enrichedConnections = [];
      for (const connection of connections) {
        const { data: childData, error: childError } = await supabase
          .from('internal_users')
          .select('id, username, role, age_group')
          .eq('id', connection.child_id)
          .eq('is_active', true)
          .single();

        if (childError) {
          console.error('❌ Error fetching child details:', childError);
          continue;
        }

        if (childData) {
          enrichedConnections.push({
            ...connection,
            child: childData
          });
        }
      }

      console.log('✅ SERVICE - Found connections:', enrichedConnections.length);
      console.log('🎯 SERVICE - Enriched connections:', enrichedConnections);
      
      return enrichedConnections;
    } catch (error) {
      console.error('❌ Error in getConnectedChildren:', error);
      return [];
    }
  },

  /**
   * Get child's teams for parent to see
   */
  async getChildTeams(childId: string) {
    try {
      const { data, error } = await supabase
        .from('team_players')
        .select(`
          *,
          team:teams(
            team_id,
            team_name,
            sport_type,
            slug,
            team_logo,
            age_group
          )
        `)
        .eq('user_id', childId);

      if (error) {
        console.error('Error fetching child teams:', error);
        return [];
      }

      return data?.map(tp => {
        const team = tp.team as any;
        return {
          team_id: team?.team_id,
          team_name: team?.team_name,
          sport_id: team?.sport_type, // Fixed: use sport_type instead of sport_id
          slug: team?.slug,
          logo_url: team?.team_logo, // Fixed: use team_logo instead of logo_url
          age_group: team?.age_group,
          is_child_team: true,
          child_id: childId
        };
      }) || [];
    } catch (error) {
      console.error('Error in getChildTeams:', error);
      return [];
    }
  }
};